import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import { FaroTextFieldMessage } from "@components/common/faro-text-field/faro-text-field-message";
import { LabelWithHelp } from "@components/common/label-with-help";
import { ChipTag } from "@components/common/members-autocomplete/chip-tag";
import { MembersAutocompleteProps } from "@components/common/members-autocomplete/members-autocomplete-types";
import {
  getOptionDisabled,
  getOptionDisabledMessage,
  getOptionLabel,
  getOptionOriginalOption,
  getOptionSubtitle,
  getOptionThumbnail,
  getOptionTitle,
  getSortedOptions,
  getUserType,
} from "@components/common/members-autocomplete/members-autocomplete-utils";
import { UserListItem } from "@components/common/members-autocomplete/user-list-item";
import { VirtualizedListbox } from "@components/common/members-autocomplete/virtualized-listbox";
import { ArrowDownIcon, blue, NO_TRANSLATE_CLASS, red } from "@flat-ui";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { Autocomplete, Popper, PopperProps } from "@mui/material";
import { getAutocompleteStyles } from "@utils/ui-utils";
import { getUserInitials } from "@utils/user-utils";
import { useMemo } from "react";

/** Threshold to enable virtualization (it can be adjusted based on performance testing) */
const VIRTUALIZATION_THRESHOLD = 100;

// TODO: Refactor to have common logics used in other components https://faro01.atlassian.net/browse/ST-1707
/**
 * This component shows an autocomplete selection that let's the user to
 * select multiple member from a list, and optionally type a valid email.
 * The component is generic enough that can be used for any purpose regarding
 * selecting multiple member. For selecting only one member see MemberAutocomplete.
 */
export function MembersAutocomplete({
  options,
  handleChange,
  onInputChange = () => undefined,
  placeHolder = "Email(s), comma separated",
  validateNewOption = () => true,
  isTeamName = () => false,
  message,
  initialValue = [],
  labelTitle,
  hasAutoFocus = false,
  isRequired = true,
  helpText = "To add multiple members/emails, simply separate them by pressing enter, semicolon, or comma.",
}: MembersAutocompleteProps): JSX.Element {
  const {
    selectedOptions,
    inputValue,
    onAutocompleteChange,
    onAutocompleteBlur,
    onAutocompleteInputChange,
    filterOptions,
  } = useMembersAutocomplete({
    initialValue,
    options,
    validateNewOption,
    isTeamName,
    handleChange,
    onInputChange,
  });

  /**
   * Virtualization adds overhead, so for small lists (under ~100 items),
   * it can be slower than just rendering the list normally. Therefore,
   * we conditionally enable virtualization only if the list is sufficiently large.
   */
  const shouldVirtualize = options.length > VIRTUALIZATION_THRESHOLD;
  const styles = getAutocompleteStyles();

  const sortedOptions = useMemo(() => getSortedOptions(options), [options]);

  return (
    <>
      {labelTitle && (
        <LabelWithHelp
          title={labelTitle}
          help={helpText}
          isRequired={isRequired}
        />
      )}
      <Autocomplete
        value={selectedOptions}
        multiple
        autoHighlight
        options={sortedOptions}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={getOptionLabel}
        onInputChange={(_, value) => onAutocompleteInputChange({ value })}
        onChange={(_, value, reason) =>
          onAutocompleteChange({
            value,
            reason,
          })
        }
        inputValue={inputValue}
        filterSelectedOptions
        freeSolo={true}
        autoSelect={false}
        clearOnBlur={false}
        popupIcon={<ArrowDownIcon />}
        onBlur={onAutocompleteBlur}
        renderInput={(params) => (
          <FaroSimpleTextField
            {...params}
            variant="outlined"
            placeholder={placeHolder}
            autoFocus={hasAutoFocus}
            sx={styles.input.sx}
          />
        )}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => (
            <ChipTag
              key={`chip-${index}`}
              tagProps={getTagProps}
              index={index}
              label={getOptionTitle(option)}
              isDisabled={getOptionDisabled(option)}
              className={NO_TRANSLATE_CLASS}
            />
          ));
        }}
        filterOptions={filterOptions}
        renderOption={(props, option, state) => (
          <li {...props} key={`listOption-${state.index}`}>
            <UserListItem
              item={{
                title: getOptionTitle(option),
                subtitle: getOptionSubtitle(option),
                userInitials: getUserInitials(getOptionOriginalOption(option)),
                userAvatarImg: getOptionThumbnail(option),
                type: getUserType(option),
              }}
              inputValue={state.inputValue}
              disabledMessage={getOptionDisabledMessage(option)}
            />
          </li>
        )}
        sx={{
          "& .MuiOutlinedInput-root": { paddingY: "2px" },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: message?.type === "error" ? red[500] : blue[600],
            },
        }}
        slots={{
          popper: (props: PopperProps) => (
            <Popper
              {...props}
              sx={{
                // Hiding the no option dropdown when `freeSolo` is false, A
                // As there is no option to disable the no option dropdown
                "& .MuiAutocomplete-noOptions": {
                  display: "none",
                },
                "& .MuiAutocomplete-listbox": {
                  padding: 0,
                  overflow: "hidden",
                },
              }}
              placement="bottom"
            />
          ),
        }}
        slotProps={{
          listbox: {
            component: shouldVirtualize ? VirtualizedListbox : undefined,
          },
        }}
      />
      <FaroTextFieldMessage message={message} />
    </>
  );
}
